/**
 * @file
 * Conversion between Markdown and HTML.
 */


const rx_lt = /</g;
const rx_gt = />/g;
const rx_space = /\t|\r|\uf8ff/g;
const rx_escape = /[\\\|`*_{}\[\]()#+\-~]/g;
const rx_escaped = /\\([\\\|`*_{}\[\]()#+\-~])/g;
const rx_br = /\\\n/g;
const rx_hr = /^([*\-=_] *){3,}$/gm;
const rx_blockquote = /\n *&gt; *([^]*?)(?=(\n|$){2})/g;
const rx_list = /\n( *)(?:[*\-+]|((\d+)|([a-z])|[A-Z])[.)]) +([^]*?)(?=(\n|$){2})/g;
const rx_listjoin = /<\/(ol|ul)>\n\n<\1>/g;
const rx_highlight = /(([*_])|(~)|(\^)|(--)|(\+\+)|`)(\1?)([^<\n]+?)\1\7(?!\1)/g;
const rx_code = /\n((```|~~~).*\n?([^]*?)\n?\2|((    .*?\n)+))/g;
const rx_link = /(!?)\[(.*?)\]\((.*?)( ".*")?\)/g;
const rx_linkauto = new RegExp(rx_link.source + '|(\\b(https?):\\/\\/[-\\w+&@#\\/%?=~|!:,.;]*[-\\w+&@#\\/%=~|])', 'g');
const rx_table = /\n(( *\|.*?\| *\n)+)/g;
const rx_thead = /^.*\n( *\|( *\:?-+\:?-+\:? *\|)* *\n|)/;
const rx_row = /.*\n/g;
const rx_cell = /\||(.*?[^\\])\|/g;
const rx_heading = /(?=^|>|\n)([>\s]*?)(#{1,6}) (.*?)( #*)?\s*?(?=\n|$)/g;
const rx_para = /(?=^|>|\n)\s*\n+([^<]+?)\n+\s*?(?=\n|<|$)/g;
const rx_stash = /-\d+\uf8ff/g;
const rx_attr = /([^>\n]+){([^}\n]*)}\s*?(?=\n|<|$)/g;
const rx_class = /\.[_a-z]+[_a-z0-9-]*/gi;


const $markdown = {

    /**
     * Convert a Markdown formatted string to HTML. This is typically used via the [v-markdown](#/directive/v-markdown)
     * directive.
     * @param {string} src - The source string in Markdown format.
     * @param {Object} options
     * @param {string} options.target - Target attribute for link tags, for example `_blank`.
     * @param {boolean} options.autolink - If `false`, do not autodetect `http://` or `https://` links.
     */
    toHTML(src, options = {}) {
        const rx_link2 = options.autolink === false ? rx_link : rx_linkauto;
        const escapeattr = src => src.replace(/"/g, '&quot;');

        const element = (tag, content) =>
            '<' + tag + '>' + content + '</' + tag + '>';

        const aelement = (str,href) =>
            '<a' + (options.target ? ' target="' + escapeattr(options.target) + '"' : '') + ' href="' + escapeattr(href) + '">' + str + '</a>';

        function br(src) {
            return src.replace(rx_br, '<br/>');
        }

        function highlight(src) {
            return src.replace(rx_highlight, function(all, p1, emp, sub, sup, small, big, p2, content) {
                return element(
                      emp ? (p2 ? 'strong' : 'em')
                    : sub ? (p2 ? 's' : 'sub')
                    : sup ? 'sup'
                    : small ? 'small'
                    : big ? 'big'
                    : 'code',
                    highlight(content));
            });
        }

        function attr(src) {
            return src.replace(rx_attr, function(all, content, attrs) {
                const cls = attrs.match(rx_class);
                return '<span'
                    + (cls ? ' class="' + cls.map(name => name.slice(1)).join(' ') + '"' : '')
                    + '>' + content + '</span>';
            });
        }

        function blockquote(src) {
            return src.replace(rx_blockquote, function(all, content) {
                return element('blockquote', blockquote(highlight(attr(content.replace(/^ *&gt; */gm, '')))));
            });
        }

        function link(src) {
            return src.replace(rx_link2, function(all, p2, p3, p4, p6, p7) {
                stash[--si] = p4
                    ? p2
                        ? '<img src="' + escapeattr(p4) + '" alt="' + escapeattr(p3) + '"/>'
                        : aelement(highlight(p3), p4)
                    : p7
                        ? aelement(p7, p7)
                        : p6;
                return si + '\uf8ff';
            });
        }

        function list(src) {
            return src.replace(rx_list, function(all, ind, ol, num, low, content) {
                var entry = element('li', br(highlight(link(attr(content.split(
                    RegExp('\n ?' + ind + '(?:(?:\\d+|[a-zA-Z]+)[.)]|[*\\-+])[^\S\r\n]', 'g')).map(list).join('</li><li>'))))));

                return '\n' + (ol
                    ? '<ol start="' + (num
                        ? ol + '">'
                        : ol == 'i' || ol == 'I'
                            ? '1" style="list-style:' + (ol == 'i' ? 'low' : 'upp') + 'er-roman">'
                            : parseInt(ol,36) - 9 + '" style="list-style:' + (low ? 'low' : 'upp') + 'er-alpha">')
                        + entry + '</ol>'
                    : element('ul', entry));
            });
        }

        function replace(rex, fn) {
            src = src.replace(rex, fn);
        }

        let stash = {};
        let si = 0;

        src = src === void 0 ? '' : '\n' + src + '\n';

        replace(rx_lt, '&lt;');
        replace(rx_gt, '&gt;');
        replace(rx_space, '  ');

        // stash escaped characters
        replace(rx_escaped, function(all, ch) {
            stash[--si] = ch;
            return si + '\uf8ff';
        });

        // stash link and image urls - data urls may be quite large
        src = link(src);

        // blockquote
        src = blockquote(src);

        // horizontal rule
        replace(rx_hr, '<hr/>');

        // list
        src = list(src);
        replace(rx_listjoin, '');

        // code
        replace(rx_code, function(all, p1, p2, p3, p4) {
            stash[--si] = element('pre', element('code', p3||p4.replace(/^    /gm, '')));
            return si + '\uf8ff';
        });

        // table
        replace(rx_table, (all, table) => {
            const sep = table.match(rx_thead)[1];
            return '\n' + element('table',
                table.replace(rx_row, (row, ri) =>
                    row == sep ? '' : element('tr', row.replace(rx_cell, (all, cell, ci) =>
                        ci ? element(sep && !ri ? 'th' : 'td', highlight(attr(cell || ''))) : ''))
                )
            )
        });

        // heading
        replace(rx_heading, (all, _, level, content) => _ + element('h' + level.length, highlight(attr(content))));

        // paragraph
        replace(rx_para, (all, content) => element('p', br(highlight(attr(content)))));

        // stash (up to two levels deep)
        const unstash = all => stash[parseInt(all)];
        replace(rx_stash, unstash);
        replace(rx_stash, unstash);

        return src.trim();
    },


    /**
     * Convert an HTML string to a Markdown approximation. This is not comprehensive but is adequate for error messages.
     * @param {string} src - The source string in HTML format.
     */
    fromHTML(src) {
        function replace(rex, fn) {
            src = src.replace(rex, fn);
        }

        const mark = {
            b: '**',
            em: '*',
            i: '*',
            s: '~~',
            strong: '**',
            sup: '^',
            big: '++',
            small: '--'
        };

        // Escape reserved characters in text nodes
        src = src.replace(/(^|>)([^<]+)(?=<|$)/g, (all, pre, text) => pre + text.replace(rx_escape, '\\$&'));

        replace(/\s+/g, ' ');
        replace(/<p( .*)?>(.+?)<\/p>/g, '\n\n$2\n\n');
        replace(/<h([1-6])( .*)?>(.+?)<\/h\1>/g, (all, size, other, content) => '\n\n' + '######'.slice(-size) + ' ' + content + '\n\n');

        replace(/<hr.*?>/g, '\n---\n');
        replace(/<br.*?>/g, '\\\n');

        replace(/<(b|em|i|s|strong|sup|big|small)( .*)?>(.+?)<\/\1>/g, (all, tag, other, content) => mark[tag] + content + mark[tag]);

        replace(/<li( .*?)>(.+?)<\/li>/g, '* $2\n');
        replace(/<a[^>]+?href=(["'])(.*?)\1.*?>(.+?)<\/a>/g, '[$3]($2)');
        replace(/<.*?>/g, '');

        return src.trim().replace(/\n{3,}/g, '\n\n');
    },


    /**
     * Escape characters in a source string for use in Markdown.
     * @param {string} src - The source string in plain text format.
     */
    escape(src) {
        return typeof src === 'string'
            ? src.replace(rx_escape, '\\$&')
            : src;
    },


    /**
     * Unescape Markdown escape characters in a source string.
     * @param {string} src - The source string in Markdown format.
     */
    unescape(src) {
        return typeof src === 'string'
            ? src.replace(rx_escaped, '$1')
            : src;
    }
};


export default $markdown;
